import { useEffect, useState, useRef } from "react"
import {
  useAnnouncements,
  isAnnouncementDismissed,
  dismissAnnouncement,
  formatAnnouncementDate,
  type Announcement,
} from "../hooks/useAnnouncements"

interface AnnouncementPopupProps {
  /** 이메일 딥링크로 진입한 세션이면 true → 공지 팝업을 건너뜀 */
  skip?: boolean
  /** 로그인·역할 확정 후 true로 전달되면 조회 시작 */
  ready?: boolean
}

/**
 * 전역 업데이트 공지 팝업.
 * - App.tsx의 로그인 이후 공통 영역에 마운트되어 진입 페이지와 무관하게 표시
 * - 세션당 1회만 조회·판정 (currentPage 변경에 반응하지 않음)
 * - '닫기'는 저장 없이 이번만 닫힘 / '다시 보지 않기'만 localStorage에 기록
 */
export function AnnouncementPopup({ skip = false, ready = false }: AnnouncementPopupProps) {
  const { fetchActiveAnnouncement } = useAnnouncements()
  const [announcement, setAnnouncement] = useState<Announcement | null>(null)
  const [open, setOpen] = useState(false)
  // 세션당 1회만 조회하기 위한 가드 (페이지 이동 시 재조회 방지)
  const checkedRef = useRef(false)

  useEffect(() => {
    if (!ready) return
    // 딥링크 진입 세션이면 조회 자체를 건너뛰고, dismiss 키도 저장하지 않음
    // → 다음 일반 접속 시 정상적으로 표시됨
    if (skip) {
      checkedRef.current = true
      return
    }
    if (checkedRef.current) return
    checkedRef.current = true

    let cancelled = false
    fetchActiveAnnouncement().then((found) => {
      if (cancelled || !found) return
      const revision = Number(found.revision || 1)
      if (isAnnouncementDismissed(found._id, revision)) return
      setAnnouncement(found)
      setOpen(true)
    })
    return () => {
      cancelled = true
    }
  }, [ready, skip, fetchActiveAnnouncement])

  // ESC 키로 닫기 (저장 없음)
  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [open])

  // 팝업이 열려 있는 동안 배경 스크롤 잠금
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  if (!open || !announcement) return null

  const handleClose = () => {
    // 이번만 닫기 — 다음 접속 시 다시 표시
    setOpen(false)
  }

  const handleDismiss = () => {
    dismissAnnouncement(announcement._id, Number(announcement.revision || 1))
    setOpen(false)
  }

  const dateLabel = formatAnnouncementDate(announcement.publishedAt || announcement.createdAt)

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={handleClose}
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="flex items-start gap-3 border-b border-amber-100 bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
            <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
              />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-amber-500 px-2 py-0.5 text-xs font-semibold text-white">
                업데이트 공지
              </span>
              <span className="text-xs text-gray-500">{dateLabel}</span>
            </div>
            <h2 className="break-words text-lg font-bold text-gray-900">{announcement.title}</h2>
          </div>
          <button
            onClick={handleClose}
            className="shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-white/70 hover:text-gray-600"
            aria-label="닫기"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 본문 */}
        <div className="max-h-[55vh] overflow-y-auto px-6 py-5">
          <div
            className="prose prose-sm max-w-none whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-700"
            dangerouslySetInnerHTML={{ __html: announcement.content || "" }}
          />
        </div>

        {/* 푸터 */}
        <div className="flex flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <button
            onClick={handleDismiss}
            className="rounded-lg px-3 py-2 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700"
          >
            다시 보지 않기
          </button>
          <button
            onClick={handleClose}
            className="rounded-lg bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-amber-600"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  )
}
